// ============================================================================
// SALES CREDIT NOTE SERVICE (ADR-0030) — entity + BU scoped.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { creditNoteEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery, appendRepeatedQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, ID,
  CreditNoteDto, CreateCreditNoteRequest, UpdateCreditNoteRequest, CreditNoteQueryParams, CreditNotePrefillDto,
  SalesNoteRegisterRowDto, SalesNoteRegisterQueryParams, DocumentLineLedgerDto, ServiceLineLedgerDto,
} from '../types';

class CreditNoteService {
  /** The sales notes register — credit notes + Sales Debit Notes (ADR-0053). */
  async getRegister(params?: SalesNoteRegisterQueryParams): Promise<PaginatedResponse<SalesNoteRegisterRowDto>> {
    const endpoint = params ? buildEndpointWithQuery(creditNoteEndpoints.register(), params) : creditNoteEndpoints.register();
    const response = await get<ApiResponse<BackendPagedResult<SalesNoteRegisterRowDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }
  async getAll(params?: CreditNoteQueryParams): Promise<PaginatedResponse<CreditNoteDto>> {
    const endpoint = params ? buildEndpointWithQuery(creditNoteEndpoints.list(), params) : creditNoteEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<CreditNoteDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }
  async getById(id: ID): Promise<CreditNoteDto> {
    const response = await get<ApiResponse<CreditNoteDto>>(creditNoteEndpoints.getById(id));
    return response.data.data;
  }
  /** The income ledger each item line reverses on the note date — shown read-only on the item line. */
  async getLineLedgers(date: string, itemIds: string[]): Promise<DocumentLineLedgerDto[]> {
    const url = appendRepeatedQuery(buildEndpointWithQuery(creditNoteEndpoints.lineLedgers(), { date }), 'itemIds', itemIds);
    const response = await get<ApiResponse<DocumentLineLedgerDto[]>>(url);
    return response.data.data || [];
  }
  /** The income ledger each service line reverses — shown read-only on the service line. */
  async getServiceLedgers(serviceIds: string[]): Promise<ServiceLineLedgerDto[]> {
    const url = appendRepeatedQuery(creditNoteEndpoints.serviceLedgers(), 'serviceIds', serviceIds);
    const response = await get<ApiResponse<ServiceLineLedgerDto[]>>(url);
    return response.data.data || [];
  }
  async getInvoicePrefill(invoiceId: ID): Promise<CreditNotePrefillDto> {
    const response = await get<ApiResponse<CreditNotePrefillDto>>(creditNoteEndpoints.invoicePrefill(invoiceId));
    return response.data.data;
  }
  async create(data: CreateCreditNoteRequest): Promise<CreditNoteDto> {
    const response = await post<ApiResponse<CreditNoteDto>>(creditNoteEndpoints.create(), data);
    return response.data.data;
  }
  async update(id: ID, data: UpdateCreditNoteRequest): Promise<CreditNoteDto> {
    const response = await put<ApiResponse<CreditNoteDto>>(creditNoteEndpoints.update(id), data);
    return response.data.data;
  }
  async remove(id: ID, rowVersion: number): Promise<void> {
    const url = buildEndpointWithQuery(creditNoteEndpoints.remove(id), { rowVersion });
    await del<ApiResponse<unknown>>(url);
  }
  async cancel(id: ID, rowVersion: number): Promise<void> {
    const url = buildEndpointWithQuery(creditNoteEndpoints.cancel(id), { rowVersion });
    await post<ApiResponse<unknown>>(url, {});
  }
}

export const creditNoteService = new CreditNoteService();
export default creditNoteService;
